import React from 'react';
import { useMobile } from '../hooks/useMobile';
import MobileLiveSession from './MobileLiveSession';
import MobileWalletConnect from './MobileWalletConnect';

/**
 * Container responsivo que orquestra a experiência mobile/desktop
 * Renderiza componentes otimizados baseado no dispositivo
 */
const ResponsiveContainer = ({ 
  children, 
  onLiveComplete, 
  onLiveError,
  onWalletConnect,
  onWalletError 
}) => {
  const isMobile = useMobile();

  // Se não for mobile, retorna children normais (fallback desktop)
  if (!isMobile) {
    return children;
  }

  // Componente wrapper para mobile
  const MobileWrapper = ({ children, className = '' }) => (
    <div className={`mobile-container mobile-safe-top mobile-safe-bottom ${className}`}>
      {children}
    </div>
  );

  // Verifica se children contém componentes específicos para substituir
  const renderMobileOptimizedContent = () => {
    if (!children) return children;

    // Se children é array, procura por componentes específicos
    const childrenArray = React.Children.toArray(children);
    
    let hasLiveSession = false;
    let hasWalletConnect = false;

    // Detecta componentes que precisam de versão mobile
    React.Children.forEach(childrenArray, child => {
      if (React.isValidElement(child)) {
        const componentName = child.type?.name || '';
        
        if (componentName.includes('LiveSession') || 
            child.props?.className?.includes('live-session')) {
          hasLiveSession = true;
        }
        
        if (componentName.includes('WalletConnect') || 
            child.props?.className?.includes('wallet-connect')) {
          hasWalletConnect = true;
        }
      }
    });

    // Substitui componentes detectados por versões mobile
    const mobileOptimizedChildren = React.Children.map(childrenArray, child => {
      if (!React.isValidElement(child)) return child;

      const componentName = child.type?.name || '';
      const className = child.props?.className || '';

      // Substitui LiveSession por MobileLiveSession
      if (componentName.includes('LiveSession') || className.includes('live-session')) {
        return (
          <MobileWrapper key="mobile-live-session">
            <MobileLiveSession
              onComplete={onLiveComplete}
              onError={onLiveError}
              onCancel={() => console.log('Live cancelada pelo usuário')}
            />
          </MobileWrapper>
        );
      }

      // Substitui WalletConnect por MobileWalletConnect
      if (componentName.includes('WalletConnect') || className.includes('wallet-connect')) {
        return (
          <MobileWrapper key="mobile-wallet-connect">
            <MobileWalletConnect
              onConnect={onWalletConnect}
              onError={onWalletError}
              showBalance={true}
            />
          </MobileWrapper>
        );
      }

      // Mantém outros componentes originais com wrapper mobile
      return (
        <MobileWrapper key={child.key}>
          {child}
        </MobileWrapper>
      );
    });

    return mobileOptimizedChildren;
  };

  return (
    <div className="responsive-container">
      {/* Mobile-Only Header */}
      <div className="mobile-only-header bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 text-center">
        <h1 className="text-xl font-bold">Plexo Natural</h1>
        <p className="text-sm opacity-90">📱 Mobile Experience</p>
      </div>

      {/* Conteúdo Mobile Otimizado */}
      <div className="mobile-content">
        {renderMobileOptimizedContent()}
      </div>

      {/* Mobile-Only Footer */}
      <div className="mobile-only-footer bg-gray-800 text-white p-3 text-center text-xs">
        <p>🎯 Otimizado para dispositivos móveis</p>
      </div>
    </div>
  );
};

/**
 * Hook para usar componentes responsivos de forma declarativa
 */
export const useResponsiveComponent = (DesktopComponent, MobileComponent) => {
  const isMobile = useMobile();
  
  return (props) => {
    if (isMobile && MobileComponent) {
      return <MobileComponent {...props} />;
    }
    return <DesktopComponent {...props} />;
  };
};

/**
 * Componentes responsivos pré-definidos
 */
export const ResponsiveLiveSession = ({ onComplete, onError }) => {
  const isMobile = useMobile();
  
  if (isMobile) {
    return (
      <MobileLiveSession 
        onComplete={onComplete} 
        onError={onError} 
      />
    );
  }
  
  const LiveSession = require('./LiveSession').default;
  return <LiveSession onComplete={onComplete} onError={onError} />;
};

export const ResponsiveWalletConnect = ({ onConnect, onError, showBalance }) => {
  const isMobile = useMobile();
  
  if (isMobile) {
    return (
      <MobileWalletConnect 
        onConnect={onConnect} 
        onError={onError}
        showBalance={showBalance}
      />
    );
  }
  
  // Importação dinâmica para evitar circular dependency
  const WalletConnect = require('./WalletConnect').default;
  return <WalletConnect onConnect={onConnect} onError={onError} />;
};

/**
 * High Order Component para tornar qualquer componente responsivo
 */
export const withResponsive = (DesktopComponent, MobileComponent) => {
  return function ResponsiveWrapper(props) {
    const isMobile = useMobile();
    
    if (isMobile && MobileComponent) {
      return <MobileComponent {...props} />;
    }
    
    return <DesktopComponent {...props} />;
  };
};

/**
 * Utilitário para condicionais mobile em JSX
 */
export const Mobile = ({ children, fallback = null }) => {
  const isMobile = useMobile();
  return isMobile ? children : fallback;
};

export const Desktop = ({ children, fallback = null }) => {
  const isMobile = useMobile();
  return !isMobile ? children : fallback;
};

export default ResponsiveContainer;
